import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownEvent } from '@shared/abstraction';
import { CheckboxEvent } from '@shared/abstraction/checkbox/checkbox-event';
import {
  ContentSearchResponse,
  SearchFilterElement,
} from '@shared/models/search-section.model';
import { HeaderService, SearchService } from '@shared/services';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';

type FilterSection = 'country' | 'section';

interface MasterCheckboxState {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
}

@Component({
  standalone: false,
  selector: 'gstar-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsComponent {
  private static isCheckboxEvent(value: unknown): value is CheckboxEvent {
    return !!value && typeof value === 'object' && 'checkboxState' in value;
  }

  private static isDropdownEvent(value: unknown): value is DropdownEvent {
    return !!value && typeof value === 'object' && 'selected' in value;
  }

  private static normalizeDropdownEvent(event: unknown): DropdownEvent | null {
    if (SearchResultsComponent.isDropdownEvent(event)) {
      return event;
    }

    if (!!event && typeof event === 'object' && 'originEvent' in event) {
      const originEvent = (event as { originEvent: unknown }).originEvent;
      if (SearchResultsComponent.isDropdownEvent(originEvent)) {
        return originEvent;
      }
    }

    return null;
  }

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly headerService = inject(HeaderService);

  // Loading state for skeletons: true while loading, false when finished.
  public isLoadingData: WritableSignal<boolean> = signal(true);

  // Filter related values
  readonly selectedFilters = signal<{
    country: Set<string>;
    section: Set<string>;
  }>({
    country: new Set<string>(),
    section: new Set<string>(),
  });
  private readonly selectedFiltersQuery = signal<string>('');

  // Pagination variables
  public pageIndex: WritableSignal<number> = signal(1); // Index of the selected page
  public itemsPerPage: WritableSignal<{ value: number; label: string }> =
    signal({ value: 10, label: '10' }); // Number of items per page
  public pageCountOptions: WritableSignal<{ value: number; label: string }[]> =
    signal([
      { value: 10, label: '10' },
      { value: 25, label: '25' },
      { value: 50, label: '50' },
    ]); // List of available page count options
  public maxPageIndex: Signal<number> = computed(() => {
    // Calculate the maximum page index based on the total number of items and items per page
    if (!this.deepSearchResultWithFilters()) return 1;
    if (this.deepSearchResultWithFilters()!.total === 0) return 1;
    return Math.ceil(
      this.deepSearchResultWithFilters()!.total / this.itemsPerPage().value
    );
  });

  public filterCountryData: Signal<SearchFilterElement[]> = computed(() =>
    this.listOfCountriesOnResults().map(country => ({
      checked: this.selectedFilters().country.has(country.name),
      name: country.name,
      comment: '',
      class: '',
      disabled: false,
    }))
  );

  public filterSectionData: Signal<SearchFilterElement[]> = computed(() =>
    this.listOfSectionsOnResults().map(section => ({
      checked: this.selectedFilters().section.has(section),
      name: section,
      comment: '',
      class: '',
      disabled: false,
    }))
  );

  readonly sectionMasterState = computed(() =>
    this.getMasterCheckboxState(
      this.listOfSectionsOnResults(),
      this.selectedFilters().section
    )
  );

  readonly countryMasterState = computed(() =>
    this.getMasterCheckboxState(
      this.listOfCountriesOnResults().map(country => country.name),
      this.selectedFilters().country
    )
  );

  constructor() {
    effect(() => {
      const query = this.query().trim();
      if (this.selectedFiltersQuery() === query) return;

      this.selectedFiltersQuery.set(query);
      this.pageIndex.set(1);
      this.selectedFilters.set({
        country: new Set<string>(),
        section: new Set<string>(),
      });
    });
  }

  readonly query: Signal<string> = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get('q') ?? '')),
    { initialValue: '' }
  );

  /**
   * Deep (BE) content search — debounced, fires only when query >= 2 chars.
   */
  private readonly deepSearch$ = toObservable(this.query).pipe(
    debounceTime(400),
    distinctUntilChanged(),
    filter(q => q.trim().length >= 2),
    switchMap(q => {
      this.isLoadingData.set(true);
      const clientId = this.headerService.selectedClient()?.id;
      return this.searchService.searchContent(q, clientId).pipe(
        tap(() => this.isLoadingData.set(false)),
        startWith(null),
        catchError(() => {
          this.isLoadingData.set(false);
          return of({ total: 0, items: [] } as ContentSearchResponse);
        })
      );
    })
  );

  /**
   * Gets all the search results from DeepSearch
   */
  readonly deepSearchResult: Signal<ContentSearchResponse | null> = toSignal(
    this.deepSearch$,
    { initialValue: null }
  );

  /**
   * Filter the deep search results based on the selected filters (country and section).
   * An empty filter group means no restriction for that group.
   * Apply pagination to the filtered results based on the current page index and items per page.
   * @returns The filtered and paginated deep search results.
   */
  readonly deepSearchResultWithFilters: Signal<ContentSearchResponse | null> =
    computed(() => {
      const result = this.deepSearchResult();
      if (!result) return null;

      const { country, section } = this.selectedFilters();

      const filteredItems = result.items.filter(
        item =>
          (country.size === 0 || country.has(item.countryName)) &&
          (section.size === 0 ||
            section.has(item.generalCountryInformationTypeName))
      );

      // Applying pagination to the filtered results
      const startIndex = (this.pageIndex() - 1) * this.itemsPerPage().value;
      const endIndex = startIndex + this.itemsPerPage().value;
      const paginatedItems = filteredItems.slice(startIndex, endIndex);

      return { total: filteredItems.length, items: paginatedItems };
    });

  readonly deepSearchTotal: Signal<number> = computed(
    () => this.deepSearchResult()?.total ?? 0
  );

  readonly filteredDeepSearchTotal: Signal<number> = computed(
    () => this.deepSearchResultWithFilters()?.total ?? 0
  );

  readonly filteredDeepSearchItems: Signal<ContentSearchResponse['items']> =
    computed(() => this.deepSearchResultWithFilters()?.items ?? []);

  /**
   * Navigates to the content page for a given country and sidebar type.
   * @param countryId The ID of the country.
   * @param typeId The ID of the subsection on the sidebar.
   */
  navigateToContent(countryId: number, typeId: number): void {
    this.router.navigate(
      [`/country/${countryId}/general-information/${typeId}`],
      {
        queryParams: { q: this.query().trim() },
      }
    );
  }

  /**
   * Bolds the search query in the result snippet for better visibility.
   * @param result The result snippet to format.
   * @returns The formatted snippet with the search query in bold.
   */
  formatResultToBoldSearchQuery(result: string): string {
    const regex = new RegExp(`(${this.query()})`, 'gi');
    return result.replace(regex, '<strong>$1</strong>');
  }

  /**
   * Retrieves a list of countries present in the search results.
   * @returns list of countries present on the deep-search results
   */
  listOfCountriesOnResults: Signal<InvestmentJurisdictionShort[]> = computed(
    () => {
      const items = this.deepSearchResult()?.items ?? [];
      const seen = new Set<number>();
      const result: InvestmentJurisdictionShort[] = [];

      for (const item of items) {
        if (!seen.has(item.countryId)) {
          seen.add(item.countryId);
          result.push({
            investmentJurisdictionId: item.countryId,
            name: item.countryName,
          });
        }
      }

      return result;
    }
  );

  /**
   * Retrieves a list of unique section names (from sidebar) present in the search results.
   * @returns list of unique section names present on the deep-search results
   */
  listOfSectionsOnResults: Signal<string[]> = computed(() => {
    const items = this.deepSearchResult()?.items ?? [];
    const seen = new Set<string>();
    const result: string[] = [];

    for (const item of items) {
      if (!seen.has(item.generalCountryInformationTypeName)) {
        seen.add(item.generalCountryInformationTypeName);
        result.push(item.generalCountryInformationTypeName);
      }
    }

    return result;
  });

  /**
   * Handles the selection of a checkbox item.
   * @param event The change event from the checkbox.
   * @param item The search filter element that was interacted with.
   * @param filterSection The section of the filter ('country' or 'section').
   */
  onCheckboxSelected(
    event: CheckboxEvent | Event,
    item: SearchFilterElement,
    filterSection: FilterSection
  ): void {
    const isChecked = SearchResultsComponent.isCheckboxEvent(event)
      ? event.checkboxState
      : item.checked;

    this.updateSelectedFilters(filterSection, item.name, isChecked);
  }

  /**
   * Handles the Enter key event for a checkbox item.
   * @param item The search filter element that was interacted with.
   * @param filterSection The section of the filter ('country' or 'section').
   */
  onEnterForCheckbox(
    item: SearchFilterElement,
    filterSection: FilterSection
  ): void {
    const isNewChecked = !item.checked;
    item.checked = isNewChecked;
    this.updateSelectedFilters(filterSection, item.name, isNewChecked);
  }

  /**
   * Selects or clears every value in one filter group while preserving the other.
   */
  toggleAllFilters(filterSection: FilterSection, checked: boolean): void {
    const values =
      filterSection === 'country'
        ? this.listOfCountriesOnResults().map(country => country.name)
        : this.listOfSectionsOnResults();

    this.pageIndex.set(1);
    this.selectedFilters.update(current => ({
      country:
        filterSection === 'country'
          ? new Set(checked ? values : [])
          : new Set(current.country),
      section:
        filterSection === 'section'
          ? new Set(checked ? values : [])
          : new Set(current.section),
    }));
  }

  /**
   * Updates the selected filters based on the user's interaction.
   * @param filterSection The section of the filter ('country' or 'section').
   * @param value The value of the filter item.
   * @param isChecked Whether the filter item is checked or not.
   */
  private updateSelectedFilters(
    filterSection: FilterSection,
    value: string,
    isChecked: boolean
  ): void {
    this.pageIndex.set(1);

    this.selectedFilters.update(current => {
      const next = {
        country: new Set(current.country),
        section: new Set(current.section),
      };

      if (isChecked) {
        next[filterSection].add(value);
      } else {
        next[filterSection].delete(value);
      }

      return next;
    });
  }

  private getMasterCheckboxState(
    values: string[],
    selectedValues: Set<string>
  ): MasterCheckboxState {
    const selectedCount = values.filter(value =>
      selectedValues.has(value)
    ).length;

    return {
      checked: values.length > 0 && selectedCount === values.length,
      indeterminate: selectedCount > 0 && selectedCount < values.length,
      disabled: values.length === 0,
    };
  }

  /**
   * Handles the change in pagination count (items per page).
   */
  onCountChange(
    event:
      | DropdownEvent
      | { originEvent: DropdownEvent; selected: unknown }
      | Event
  ): void {
    const normalizedEvent =
      SearchResultsComponent.normalizeDropdownEvent(event);

    const selectedCount =
      Number(normalizedEvent?.selected?.value) || this.itemsPerPage().value;

    this.itemsPerPage.set({
      value: selectedCount,
      label: String(selectedCount),
    });
    this.pageIndex.set(1); // Reset to the first page when count changes
  }

  /**
   * Updates the current page index in the paginator.
   * @param newPage The new page index to set.
   */
  updatePaginatorStep(newPage: number | Event): void {
    if (typeof newPage !== 'number') {
      return;
    }

    this.pageIndex.set(newPage);
  }
}
